/**
 * Hook to register the Addresses ("ที่อยู่") tab with DetailPageTabsContext.
 *
 * Adds an Addresses tab to the Customer detail page only — every delivery
 * address the customer has used across channels (Shopee / Lazada / Storefront).
 * Mirrors useLoyaltyTab exactly (functional setTabs updater, no `tabs`
 * dependency, self-removing). Content is rendered separately in ExperienceView.
 */

import { useEffect } from "react";
import { useDetailPageTabs } from "@/contexts/DetailPageTabsContext";
import type { TabStructure } from "@/features/inventory/item-detail/utils/buildTabsFromMeta";
import { LOYALTY_TAB_ID } from "./useLoyaltyTab";

/** Tab ID constant — reference this everywhere the Addresses tab is used. */
export const ADDRESSES_TAB_ID = "addresses";

interface UseAddressesTabOptions {
  doctype: string;
  name?: string;
  enabled?: boolean;
}

export function useAddressesTab({ doctype, name, enabled = true }: UseAddressesTabOptions) {
  const { setTabs } = useDetailPageTabs();
  const addressesLabel = "ที่อยู่";

  useEffect(() => {
    const isCustomerDoctype = doctype === "Customer";
    const shouldShow = enabled && isCustomerDoctype && !!name && name !== "new";

    setTabs((prevTabs: TabStructure[]) => {
      const exists = prevTabs.some((t) => t.name === ADDRESSES_TAB_ID);

      const addressesTab: TabStructure = {
        name: ADDRESSES_TAB_ID,
        label: addressesLabel,
        sections: [], // Empty — content is rendered separately in ExperienceView
      };

      if (shouldShow) {
        if (!exists) {
          // Insert right after the Loyalty tab when present, else append.
          const loyaltyIdx = prevTabs.findIndex((t) => t.name === LOYALTY_TAB_ID);
          if (loyaltyIdx !== -1) {
            const newTabs = [...prevTabs];
            newTabs.splice(loyaltyIdx + 1, 0, addressesTab);
            return newTabs;
          }
          return [...prevTabs, addressesTab];
        }

        // Keep label in sync (language switch)
        const currentIdx = prevTabs.findIndex((t) => t.name === ADDRESSES_TAB_ID);
        if (currentIdx !== -1 && prevTabs[currentIdx].label !== addressesLabel) {
          const newTabs = [...prevTabs];
          newTabs[currentIdx] = { ...prevTabs[currentIdx], label: addressesLabel };
          return newTabs;
        }
        return prevTabs;
      }

      if (!shouldShow && exists) {
        return prevTabs.filter((t) => t.name !== ADDRESSES_TAB_ID);
      }

      return prevTabs;
    });
  }, [doctype, name, enabled, setTabs, addressesLabel]);

  return {
    tabId: ADDRESSES_TAB_ID,
    isEnabled: enabled && doctype === "Customer" && !!name && name !== "new",
  };
}
